import React, { useState } from "react";
import { Tag, Check, Loader2, X } from "lucide-react";
import api from "../api";
import { useLang } from "../context/LanguageContext";

// One field, used at both real checkouts. The server re-checks the code when the session is
// created, so this is only ever a preview of what they'll pay.
export const PromoCodeField = ({ promo, onApply, compact = false, renewNote = "" }) => {
  const { t } = useLang();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const apply = async () => {
    const code = value.trim().toUpperCase();
    if (!code) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/discounts/validate", { code });
      onApply({ ...data, code });
      setValue("");
    } catch (e) { setError(e.response?.data?.detail || t("pc.invalid")); }
    finally { setBusy(false); }
  };

  if (promo) {
    return (
      <div className={compact ? "" : "mt-2"} data-testid="promo-applied">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#16a34a] bg-[#e7f6ec] dark:bg-[#16341f] px-3 py-1.5 rounded-xl">
            <Check size={13} /> {promo.type === "percent" ? t("pc.appliedPct", { code: promo.code, n: promo.value }) : t("pc.appliedAmt", { code: promo.code, n: promo.value })}
          </span>
          <button onClick={() => onApply(null)} data-testid="promo-remove" aria-label={t("pc.remove")}
            className="h-7 w-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"><X size={13} /></button>
        </div>
        {renewNote && <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="promo-renew-note">{renewNote}</p>}
      </div>
    );
  }

  return (
    <div className={compact ? "" : "mt-2"}>
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-[240px]">
          <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={value} onChange={(e) => { setValue(e.target.value); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") apply(); }}
            placeholder={t("pc.placeholder")} data-testid="promo-input"
            className="w-full bg-secondary rounded-xl pl-9 pr-3 py-2 text-sm font-semibold uppercase tracking-wide outline-none focus:ring-2 focus:ring-[#16a34a]/40" />
        </div>
        <button onClick={apply} disabled={busy || !value.trim()} data-testid="promo-apply"
          className="text-xs font-bold px-4 py-2.5 rounded-xl border border-border hover:bg-secondary disabled:opacity-50 transition-colors flex items-center gap-1.5">
          {busy ? <Loader2 className="animate-spin" size={13} /> : null} {t("pc.apply")}
        </button>
      </div>
      {error && <p className="text-xs font-semibold text-rose-600 mt-1.5" data-testid="promo-error">{error}</p>}
    </div>
  );
};

// Shared maths so the struck-through prices match what the server will charge.
export const promoPrice = (amount, promo, packageId) => {
  if (!promo) return null;
  if (promo.packages?.length && packageId && !promo.packages.includes(packageId)) return null;
  const off = promo.type === "percent" ? (amount * promo.value) / 100 : promo.value;
  return Math.max(0, Math.round((amount - off) * 100) / 100);
};

export default PromoCodeField;
